import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import Image from "../elements/Image";
import Button from "../form/Button";
import { useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { wishListManage } from "../../services/wishlistService";
import ReviewRatingModal from "../common/ReviewRatingModal";
import ReviewPreview from "../common/ReviewPreview";
import RatingStars from "../common/RatingStars";
import { lngDetect } from "../../helpers/lngDetect";

const EventItem = ({ data, addToFavoriteResponse = () => {} }) => {
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const { lang } = useSelector((state) => state?.langProvider);
  const { data: organizationData } = useSelector(
    (state) => state?.organization?.selectedOrganizationDetails
  );
  const { data: usrData } = useSelector((state) => state?.profile);
  const navigate = useNavigate();

  const { mutate: wishlistManage, isPending: isLoading } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        ...val,
      };
      return await wishListManage(payload);
    },
    onSuccess: (res) => {
      addToFavoriteResponse(res);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handleFavourite = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!usrData) {
      toast.info("Please login to manage your wishlist");
      navigate("/login");
      return;
    }
    wishlistManage({
      referenceId: data?._id,
      user: usrData?._id,
      organization: organizationData?._id,
    });
  };

  const handleOpenReviewModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsReviewModalOpen(true);
  };

  return (
    <Link
      to={`/${lang}${data?.link}` || "#"}
      className="w-full flex flex-col rounded-xl overflow-hidden shadow-md relative transition-all duration-300 border hover:border-gradient-red bg-white"
    >
      {/* Image Slider Section */}
      <div className="relative w-full z-[1] flex-shrink-0">
        {data?.images?.length > 0 ? (
          data.images.length > 1 ? (
            <Swiper
              slidesPerView={1}
              autoplay={{
                delay: 2500,
                disableOnInteraction: false,
              }}
              pagination={{ clickable: true }}
              breakpoints={{
                320: { slidesPerView: 1 },
              }}
              modules={[Autoplay, Pagination]}
              className="w-full eventSlider"
            >
              {data.images.map((img, index) => (
                <SwiperSlide key={index}>
                  <div className="w-full aspect-[7/5]">
                    <Image src={img} classes="w-full h-full object-cover" />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="w-full aspect-[7/5]">
              <Image
                src={data?.images[0]}
                classes="w-full h-full object-cover"
              />
            </div>
          )
        ) : (
          <div className="w-full aspect-[7/5] bg-slate-100"></div>
        )}

        {/* Favourite Button */}
        <div className="absolute right-3 top-3 z-[4]">
          <Button
            buttonFunction={handleFavourite}
            buttonIcon={
              data?.isWishlist ? "fa-solid fa-heart" : "fa-regular fa-heart"
            }
            buttonIconPosition="left"
            buttonClasses="w-9 aspect-square bg-white rounded-full flex items-center justify-center text-gradient-red"
          />
        </div>

        {/* {data?.isFollowing && (
          <div className="absolute left-3 top-3 z-[4]">
            <div className="px-3 py-1 bg-green-500 text-white rounded-full text-xs font-medium flex items-center gap-1">
              <i className="fa-solid fa-check"></i>
              Following
            </div>
          </div>
        )} */}
      </div>

      {/* Content Section */}
      <div className="py-5 px-5 relative flex-shrink flex-grow flex flex-col">
        {data?.eventTitle && (
          <div className="text-base lg:text-lg text-deep-purple font-bold line-clamp-2 mb-4 uppercase">
            {lngDetect(data?.eventTitle)}
          </div>
        )}
        {data?.rating && data.rating > 0 && (
          <div className="mb-4 p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-100">
            <RatingStars
              rating={data.rating}
              totalReviews={data.totalReviews || data.reviewCount || 0}
              showCount={true}
              size="lg"
            />

            {/* Write Review Button */}
            <Button
              buttonFunction={handleOpenReviewModal}
              buttonIcon="fa-regular fa-star"
              buttonIconPosition="left"
              buttonLabel="Review"
              buttonClasses="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-700 text-xs font-medium rounded-lg transition-all duration-200 border border-amber-200"
            />
          </div>
        )}
        {data?.eventDate && (
          <div className="text-text-secondary text-sm mb-2 flex items-start gap-3">
            <span className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/10">
              <i className="fa-regular fa-calendar-days text-primary"></i>
            </span>
            <span className="flex flex-shrink flex-grow pt-1">
              {data?.eventDate}
            </span>
          </div>
        )}
        {data?.eventLocation && (
          <div className="text-text-secondary text-sm mb-4 flex items-start gap-3">
            <span className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/10">
              <i className="fa-regular fa-location-dot text-primary"></i>
            </span>
            <span className="flex flex-shrink flex-grow pt-1">
              {data?.eventLocation}
            </span>
          </div>
        )}

        {data?.latestReview && <ReviewPreview review={data.latestReview} />}

        {data?.tags?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {data?.tags.slice(0, 2).map((tag, index) => (
              <div
                key={index}
                className="h-8 rounded-full px-4 flex items-center justify-center text-xs font-medium border border-purple-400 text-purple-700"
              >
                {lngDetect(tag)}
              </div>
            ))}
            {data?.tags.length > 2 && (
              <div className="h-8 aspect-square rounded-full border border-text-secondary flex items-center justify-center text-black text-xs font-medium">
                +{data?.tags.length - 2}
              </div>
            )}
          </div>
        )}

        {data?.eventMinRate && (
          <div className="text-deep-purple text-sm uppercase mt-auto">
            From{" "}
            <span className="text-deep-purple text-2xl uppercase font-bold">
              {data?.currency?.symbol || "$"} {data?.eventMinRate}
            </span>
          </div>
        )}
      </div>

      {/* Review Rating Modal */}
      <ReviewRatingModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        event={data}
      />
    </Link>
  );
};

export default EventItem;
